import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { LanguageSwitcher, LinkButton } from './components';
import { I18nProvider, useI18n, type Locale, type TranslationKey } from './i18n';
import { SessionProvider, useSession } from './auth/session-context';
import { usersApi } from './api/client';
import { AccountPage } from './pages/AccountPage';
import { BalancePage } from './pages/BalancePage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignPage } from './pages/campaign/CampaignPage';
import { CallersPage } from './pages/CallersPage';
import { DashboardPage } from './pages/DashboardPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/PasswordPages';
import { PlatformLoginPage } from './pages/platform/PlatformLoginPage';
import { PlatformSettingsPage } from './pages/platform/PlatformSettingsPage';
import { PlatformTenantPage } from './pages/platform/PlatformTenantPage';
import { PlatformTenantsPage } from './pages/platform/PlatformTenantsPage';
import { ResetPasswordPage } from './pages/PasswordPages';
import { RegisterPage } from './pages/RegisterPage';
import { StatisticsPage } from './pages/StatisticsPage';

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AppRoot />
    </BrowserRouter>
  );
}

/**
 * Anwendung ohne eigenen Router - so kann der Test einen MemoryRouter
 * darumlegen.
 */
export function AppRoot(): JSX.Element {
  return (
    <I18nProvider>
      <SessionProvider>
        <Router />
      </SessionProvider>
    </I18nProvider>
  );
}

/**
 * Navigationspunkte des Kundenadmins - genau die sechs aus
 * 02_navigation_ux_tutorial_i18n.md. Bereiche, die noch nicht fertig sind,
 * stehen schon in der Navigation und führen auf eine Seite, die das klar
 * sagt; tote Links gibt es keine.
 */
const ADMIN_NAV: { to: string; label: TranslationKey }[] = [
  { to: '/', label: 'nav.dashboard' },
  { to: '/kampagnen', label: 'nav.campaigns' },
  { to: '/caller', label: 'nav.callers' },
  { to: '/statistik', label: 'nav.statistics' },
  { to: '/guthaben', label: 'nav.balance' },
  { to: '/einstellungen', label: 'nav.settings' },
];

/**
 * Navigation des Systemadmins (10_main_admin.md).
 *
 * Nur zwei Punkte, und ausdruecklich kein Zugang zu einzelnen
 * Kundendatensaetzen: der Systemadmin verwaltet Konten und holt Daten als
 * Export, er arbeitet nicht in den Daten der Kunden.
 */
const PLATFORM_NAV: { to: string; label: TranslationKey }[] = [
  { to: '/systemadmin/kunden', label: 'platform.nav.tenants' },
  { to: '/systemadmin/einstellungen', label: 'platform.nav.settings' },
];

/**
 * Navigation eines Callers. Bewusst getrennt: ein Caller sieht nichts, was
 * er ohnehin nicht darf, und keine leeren Adminbereiche.
 */
const CALLER_NAV: { to: string; label: TranslationKey }[] = [
  { to: '/', label: 'nav.home' },
  { to: '/kampagnen', label: 'nav.campaigns' },
  { to: '/einstellungen', label: 'nav.settings' },
];

function Router(): JSX.Element {
  const { principal, loading } = useSession();
  const { t } = useI18n();
  const location = useLocation();

  if (loading) {
    return (
      <main className="page page-schmal">
        <p className="lead">{t('app.loading')}</p>
      </main>
    );
  }

  if (!principal) {
    // Ohne Anmeldung sind nur die öffentlichen Seiten erreichbar. Welche Daten
    // jemand sehen darf, entscheidet ohnehin der Server.
    return (
      <Routes>
        <Route path="/anmelden" element={<LoginPage />} />
        <Route path="/verwaltung" element={<PlatformLoginPage />} />
        <Route path="/registrieren" element={<RegisterPage />} />
        <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
        <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/anmelden" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  // Der Systemadmin hat einen eigenen Bereich mit eigener Navigation. Die
  // Mandantenoberflaeche bekommt er bewusst nicht zu sehen.
  if (principal.kind === 'platform_admin') {
    return (
      <div className="app">
        <AppHeader />
        <Routes>
          <Route path="/systemadmin/kunden" element={<PlatformTenantsPage />} />
          <Route path="/systemadmin/kunden/:tenantId" element={<PlatformTenantPage />} />
          <Route path="/systemadmin/einstellungen" element={<PlatformSettingsPage />} />
          <Route path="*" element={<Navigate to="/systemadmin/kunden" replace />} />
        </Routes>
      </div>
    );
  }

  const isAdmin = principal.kind === 'tenant_user' && principal.isAdmin;

  return (
    <div className="app">
      <AppHeader />
      <Routes>
        <Route path="/" element={isAdmin ? <DashboardPage /> : <HomePage />} />
        <Route path="/kampagnen" element={<CampaignsPage />} />
        <Route path="/kampagnen/:campaignId" element={<CampaignPage />} />
        <Route path="/caller" element={isAdmin ? <CallersPage /> : <Navigate to="/" replace />} />
        <Route path="/statistik" element={isAdmin ? <StatisticsPage /> : <Navigate to="/" replace />} />
        <Route path="/guthaben" element={isAdmin ? <BalancePage /> : <Navigate to="/" replace />} />
        <Route path="/einstellungen" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

/**
 * Kopfzeile mit Navigation, Sprachumschalter und Abmelden.
 * Der Sprachumschalter steht - wie vorgegeben - oben rechts neben dem
 * Abmelden (02_navigation_ux_tutorial_i18n.md).
 */
function AppHeader(): JSX.Element {
  const { t } = useI18n();
  const { principal, logout } = useSession();

  const isPlatform = principal?.kind === 'platform_admin';
  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;
  const punkte = isPlatform ? PLATFORM_NAV : isAdmin ? ADMIN_NAV : CALLER_NAV;

  const saveLocale = (locale: Locale): void => {
    // Die Sprache gehört zum Konto und wird serverseitig gespeichert.
    void usersApi.setLocale(locale).catch(() => undefined);
  };

  const marke = isPlatform ? t('app.name') + ' · ' + t('platform.role') : t('app.name');

  return (
    <header className="app-header">
      <span className="app-marke">{marke}</span>

      <nav className="app-nav" aria-label={t(isPlatform ? 'platform.nav.main' : 'nav.main')}>
        {punkte.map((punkt) => (
          <NavLink
            key={punkt.to}
            to={punkt.to}
            end={punkt.to === '/'}
            className={({ isActive }) => (isActive ? 'nav-aktiv' : undefined)}
          >
            {t(punkt.label)}
          </NavLink>
        ))}
      </nav>

      <div className="app-header-right">
        <LinkButton onClick={() => void logout()}>{t('action.logout')}</LinkButton>
        <LanguageSwitcher onChange={saveLocale} />
      </div>
    </header>
  );
}
